import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  Divider, IconButton, Tabs, Tab, TextField, MenuItem, Stack, Avatar,
  List, ListItem, ListItemText, ListItemIcon, Tooltip, LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Phone as PhoneIcon, Email as EmailIcon,
  Add as AddIcon, CalendarMonth as CalendarIcon,
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Receipt as ReceiptIcon, PaymentOutlined as PayIcon,
  MarkEmailRead as EmailSentIcon, Sms as SmsIcon,
  UploadFile as UploadIcon, InsertDriveFile as FileIcon, Download as DownloadIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Client {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_primary: string;
  phone_secondary: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  client_type: string;
  preferred_communication: string;
  preferred_payment_method: string;
  notes: string;
  created_at: string;
}

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone_primary: string;
  phone_secondary: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  client_type: string;
  preferred_communication: string;
  preferred_payment_method: string;
  notes: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_primary?: string;
}

interface Pet {
  id: number;
  name: string;
  species: string;
  breed: string;
  date_of_birth: string;
  status: string;
}

interface Appointment {
  id: number;
  appointment_date: string;
  appointment_time_start: string;
  status: string;
  reason_for_visit: string;
  veterinarian_name: string;
  service_name: string;
  pet_name: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  payment_status: string;
  pet_name: string;
}

// Minimal stub for communications log (extend with real API when available)
interface CommLog {
  id: string;
  type: 'email' | 'sms' | 'phone';
  subject: string;
  sent_at: string;
  status: 'sent' | 'delivered' | 'failed';
}

// Minimal stub for uploaded files
interface ClientFile {
  id: string;
  name: string;
  size: number;
  uploaded_at: string;
  url?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d\s()\-]{6,20}$/;

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

function clientToForm(c: Client): FormState {
  return {
    first_name: c.first_name ?? '',
    last_name: c.last_name ?? '',
    email: c.email ?? '',
    phone_primary: c.phone_primary ?? '',
    phone_secondary: c.phone_secondary ?? '',
    address_line1: c.address_line1 ?? '',
    address_line2: c.address_line2 ?? '',
    city: c.city ?? '',
    state_province: c.state_province ?? '',
    postal_code: c.postal_code ?? '',
    country: c.country ?? '',
    client_type: c.client_type ?? 'individual',
    preferred_communication: c.preferred_communication ?? 'email',
    preferred_payment_method: c.preferred_payment_method ?? 'cash',
    notes: c.notes ?? '',
  };
}

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.first_name.trim()) errors.first_name = 'First name is required';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required';
  if (form.email && !EMAIL_RE.test(form.email)) errors.email = 'Invalid email address';
  if (form.phone_primary && !PHONE_RE.test(form.phone_primary)) errors.phone_primary = 'Invalid phone number';
  return errors;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Completed': case 'Paid': case 'active': return 'success';
    case 'Scheduled': return 'info';
    case 'Confirmed': return 'primary';
    case 'Cancelled': return 'error';
    case 'Unpaid': return 'warning';
    default: return 'default';
  }
};

// Deterministic avatar color from name
const avatarColor = (name: string) => {
  const colors = ['#1976d2', '#388e3c', '#7b1fa2', '#d32f2f', '#f57c00', '#0288d1', '#303f9f'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const speciesEmoji = (species: string | null | undefined) => {
  const s = (species ?? '').toLowerCase();
  if (s === 'cat' || s === 'feline') return '🐱';
  if (s === 'dog' || s === 'canine') return '🐶';
  if (s === 'rabbit') return '🐰';
  if (s.includes('bird') || s === 'avian') return '🐦';
  if (s === 'fish') return '🐟';
  return '🐾';
};

// Outstanding Balance stat card — actionable when balance > 0
const BalanceCard = ({
  balance,
  unpaidCount,
  onSettle,
}: {
  balance: number;
  unpaidCount: number;
  onSettle: () => void;
}) => {
  const hasBalance = balance > 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1 }}>
      <Typography
        variant="h5"
        fontWeight={700}
        color={hasBalance ? 'error.main' : 'text.primary'}>
        ${balance.toFixed(2)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">Outstanding Balance</Typography>
      {hasBalance && (
        <>
          <Typography variant="caption" color="error">{unpaidCount} unpaid invoice{unpaidCount !== 1 ? 's' : ''}</Typography>
          <Box mt={1}>
            <Button size="small" variant="contained" color="error" startIcon={<PayIcon />}
              onClick={onSettle}>
              Settle Account
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

const QuickStatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', flex: 1 }}>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    {sub && <Typography variant="caption" color="primary">{sub}</Typography>}
  </Paper>
);

// ── Communications tab ──────────────────────────────────────────────────────
const CommTypeIcon = ({ type }: { type: CommLog['type'] }) => {
  if (type === 'sms') return <SmsIcon fontSize="small" color="action" />;
  if (type === 'phone') return <PhoneIcon fontSize="small" color="action" />;
  return <EmailSentIcon fontSize="small" color="action" />;
};

const CommunicationsTab = ({ clientId }: { clientId: number }) => {
  const [logs, setLogs] = useState<CommLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Attempt to load reminders/comm log from backend; gracefully fall back to empty
    api.get(`/clients/${clientId}/communications`).then((res: any) => {
      setLogs(res?.data ?? res ?? []);
    }).catch(() => {
      setLogs([]);
    }).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <Box py={4} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;

  if (logs.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <EmailSentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No communications logged yet.</Typography>
        <Typography variant="body2" color="text.disabled" mt={0.5}>
          Automated reminders and manual messages will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {logs.map(log => (
        <ListItem key={log.id} divider sx={{ px: 0 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CommTypeIcon type={log.type} />
          </ListItemIcon>
          <ListItemText
            primary={log.subject}
            secondary={fmtDateTime(log.sent_at)}
          />
          <Chip
            label={log.status}
            size="small"
            color={log.status === 'delivered' || log.status === 'sent' ? 'success' : 'error'}
            variant="outlined"
          />
        </ListItem>
      ))}
    </List>
  );
};

// ── Files / Documents tab ───────────────────────────────────────────────────
const FilesTab = ({ clientId }: { clientId: number }) => {
  const [files, setFiles] = useState<ClientFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchFiles = useCallback(() => {
    api.get(`/clients/${clientId}/files`).then((res: any) => {
      setFiles(res?.data ?? res ?? []);
    }).catch(() => {
      setFiles([]);
    }).finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadProgress(0);

    // Simulate progress while uploading
    const interval = setInterval(() => setUploadProgress(p => Math.min(p + 20, 90)), 200);

    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/clients/${clientId}/files`, fd);
      fetchFiles();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      clearInterval(interval);
      setUploadProgress(0);
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await api.delete(`/clients/${clientId}/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch {
      setError('Failed to delete file.');
    }
  };

  if (loading) return <Box py={4} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <input
          ref={inputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleUpload}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        />
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload Document'}
        </Button>
      </Box>

      {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />}

      {files.length === 0 ? (
        <Box py={6} textAlign="center">
          <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No documents uploaded yet.</Typography>
          <Typography variant="body2" color="text.disabled" mt={0.5}>
            Upload consent forms, waivers, or ID copies here.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map(f => (
                <TableRow key={f.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FileIcon fontSize="small" color="action" />
                      {f.name}
                    </Box>
                  </TableCell>
                  <TableCell>{fmtBytes(f.size)}</TableCell>
                  <TableCell>{fmtDate(f.uploaded_at)}</TableCell>
                  <TableCell align="right">
                    {f.url && (
                      <Tooltip title="Download">
                        <IconButton size="small" href={f.url} target="_blank" rel="noopener">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(f.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const ClientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);
      const [clientRes, petsRes, apptsRes, invoicesRes] = await Promise.all([
        api.get<Client>(API_ENDPOINTS.CLIENTS.GET(id!)),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.CLIENTS.PETS(id!)),
        api.get<{ data: Appointment[] }>(`/appointments?client_id=${id}&limit=50`),
        api.get<{ data: Invoice[] }>(`/invoicing/invoices?client_id=${id}`),
      ]);
      setClient(clientRes);
      setPets(petsRes.data ?? []);
      setAppointments(apptsRes.data ?? []);
      setInvoices(invoicesRes.data ?? []);
    } catch {
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) fetchClientData(); }, [id, fetchClientData]);

  const startEdit = () => {
    if (!client) return;
    setForm(clientToForm(client));
    setFormErrors({});
    setSaveError('');
    setSaveSuccess(false);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setForm(null);
    setFormErrors({});
    setSaveError('');
  };

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => prev ? { ...prev, [field]: e.target.value } : prev);
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = async () => {
    if (!form) return;
    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const updated = await api.put<Client>(API_ENDPOINTS.CLIENTS.UPDATE(id!), form);
      setClient(updated);
      setEditMode(false);
      setForm(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setSaveError(msg || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box>
        <Alert severity="error">{error || 'Client not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mt: 2 }}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  const fullName = `${client.first_name} ${client.last_name}`;
  const initials = `${client.first_name?.[0] ?? ''}${client.last_name?.[0] ?? ''}`.toUpperCase();
  const today = new Date().toISOString().slice(0, 10);
  const nextAppt = appointments
    .filter(a => a.appointment_date >= today && a.status !== 'Cancelled')
    .sort((a, b) => a.appointment_date.localeCompare(b.appointment_date))[0];
  const outstandingBalance = invoices.reduce((sum, inv) => sum + (Number(inv.balance_due) || 0), 0);
  const lifetimeSpend = invoices.reduce((sum, inv) => sum + (Number(inv.amount_paid) || 0), 0);
  const unpaidCount = invoices.filter(i => Number(i.balance_due) > 0).length;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <IconButton onClick={() => navigate('/clients')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Clients &rsaquo; <strong>{fullName}</strong>
        </Typography>
      </Box>

      {/* Hero row */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ width: 64, height: 64, bgcolor: avatarColor(fullName), fontSize: 24, fontWeight: 700 }}>
          {initials}
        </Avatar>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" fontWeight={700}>{fullName}</Typography>
            <Chip label={client.client_type ?? 'individual'} size="small"
              color={client.client_type === 'individual' ? 'primary' : 'secondary'}
              sx={{ textTransform: 'capitalize' }} />
          </Box>
          <Box display="flex" gap={2} mt={0.5}>
            {client.email && (
              <Typography variant="body2" color="text.secondary">
                <a href={`mailto:${client.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <EmailIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {client.email}
                </a>
              </Typography>
            )}
            {client.phone_primary && (
              <Typography variant="body2" color="text.secondary">
                <a href={`tel:${client.phone_primary}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {client.phone_primary}
                </a>
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
          {editMode ? (
            <>
              <Button variant="outlined" size="small" startIcon={<CancelIcon />} onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button variant="contained" size="small" startIcon={saving ? undefined : <SaveIcon />}
                onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={startEdit}>
                Edit
              </Button>
              <Button variant="outlined" size="small" startIcon={<CalendarIcon />}
                onClick={() => navigate('/appointments')}>
                Book Appointment
              </Button>
              <Button variant="outlined" size="small" startIcon={<AddIcon />}
                onClick={() => navigate(`/patients/new?client_id=${id}`)}>
                Add Pet
              </Button>
              <Button variant="contained" size="small" startIcon={<ReceiptIcon />}
                onClick={() => navigate('/invoicing')}>
                Generate Invoice
              </Button>
            </>
          )}
        </Box>
      </Box>

      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Client updated successfully.</Alert>}

      {/* Quick stats */}
      <Box display="flex" gap={2} mb={3}>
        <QuickStatCard label="Total Pets" value={pets.length} />
        <QuickStatCard
          label="Next Appointment"
          value={nextAppt ? fmtDate(nextAppt.appointment_date) : 'None'}
          sub={nextAppt ? nextAppt.pet_name : undefined}
        />
        <BalanceCard
          balance={outstandingBalance}
          unpaidCount={unpaidCount}
          onSettle={() => navigate('/invoicing')}
        />
        <QuickStatCard label="Lifetime Spend" value={`$${lifetimeSpend.toFixed(2)}`} />
      </Box>

      <Grid container spacing={3}>
        {/* Left: Contact / Edit Form */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            {editMode && form ? (
              <>
                <Typography variant="h6" gutterBottom>Edit Client</Typography>
                <Divider sx={{ mb: 2 }} />
                {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

                <Stack spacing={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      label="First Name *" fullWidth size="small"
                      value={form.first_name} onChange={handleChange('first_name')}
                      error={!!formErrors.first_name} helperText={formErrors.first_name}
                    />
                    <TextField
                      label="Last Name *" fullWidth size="small"
                      value={form.last_name} onChange={handleChange('last_name')}
                      error={!!formErrors.last_name} helperText={formErrors.last_name}
                    />
                  </Box>

                  <TextField
                    label="Email" fullWidth size="small" type="email"
                    value={form.email} onChange={handleChange('email')}
                    error={!!formErrors.email} helperText={formErrors.email}
                  />

                  <TextField
                    label="Primary Phone" fullWidth size="small"
                    value={form.phone_primary} onChange={handleChange('phone_primary')}
                    error={!!formErrors.phone_primary} helperText={formErrors.phone_primary}
                    placeholder="+61 4XX XXX XXX"
                  />

                  <TextField
                    label="Secondary / Emergency Phone" fullWidth size="small"
                    value={form.phone_secondary} onChange={handleChange('phone_secondary')}
                    placeholder="Backup contact number"
                  />

                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">Address</Typography>

                  <TextField label="Address Line 1" fullWidth size="small" value={form.address_line1} onChange={handleChange('address_line1')} />
                  <TextField label="Address Line 2" fullWidth size="small" value={form.address_line2} onChange={handleChange('address_line2')} />

                  <Box display="flex" gap={1}>
                    <TextField label="City" fullWidth size="small" value={form.city} onChange={handleChange('city')} />
                    <TextField label="State / Province" fullWidth size="small" value={form.state_province} onChange={handleChange('state_province')} />
                  </Box>

                  <Box display="flex" gap={1}>
                    <TextField label="Zip / Postal Code" fullWidth size="small" value={form.postal_code} onChange={handleChange('postal_code')} />
                    <TextField label="Country" fullWidth size="small" value={form.country} onChange={handleChange('country')} />
                  </Box>

                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">Preferences</Typography>

                  <TextField select label="Client Type" fullWidth size="small" value={form.client_type} onChange={handleChange('client_type')}>
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="organization">Organization</MenuItem>
                  </TextField>

                  <TextField select label="Preferred Communication" fullWidth size="small" value={form.preferred_communication} onChange={handleChange('preferred_communication')}>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                  </TextField>

                  <TextField select label="Preferred Payment" fullWidth size="small" value={form.preferred_payment_method} onChange={handleChange('preferred_payment_method')}>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="insurance">Insurance</MenuItem>
                  </TextField>

                  <TextField
                    label="Notes" fullWidth size="small" multiline rows={3}
                    value={form.notes} onChange={handleChange('notes')}
                  />
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <Divider sx={{ mb: 2 }} />

                {client.email && (
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <a href={`mailto:${client.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      <Typography>{client.email}</Typography>
                    </a>
                  </Box>
                )}

                {client.phone_primary && (
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <a href={`tel:${client.phone_primary}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      <Typography>{client.phone_primary}</Typography>
                    </a>
                  </Box>
                )}

                {client.phone_secondary && (
                  <Box display="flex" alignItems="center" mb={1.5}>
                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <a href={`tel:${client.phone_secondary}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      <Typography variant="body2" color="text.secondary">{client.phone_secondary} (Alt)</Typography>
                    </a>
                  </Box>
                )}

                {(client.address_line1 || client.city) && (
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {client.address_line1 && <div>{client.address_line1}</div>}
                    {client.address_line2 && <div>{client.address_line2}</div>}
                    {client.city && <div>{client.city}{client.state_province ? `, ${client.state_province}` : ''} {client.postal_code}</div>}
                    {client.country && <div>{client.country}</div>}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary">Preferred Communication</Typography>
                <Typography sx={{ mt: 0.5, mb: 1.5, textTransform: 'capitalize' }}>{client.preferred_communication || 'Not set'}</Typography>

                <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                <Typography sx={{ mt: 0.5, mb: 1.5, textTransform: 'capitalize' }}>{client.preferred_payment_method || 'Not set'}</Typography>

                {client.notes && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{client.notes}</Typography>
                  </>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">Client Since</Typography>
                <Typography sx={{ mt: 0.5 }}>{fmtDate(client.created_at)}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Right: Activity Tabs */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={`Pets (${pets.length})`} />
              <Tab label={`Appointments (${appointments.length})`} />
              <Tab label={`Invoices (${invoices.length})`} />
              <Tab label="Communications" />
              <Tab label="Files / Documents" />
            </Tabs>

            {/* Summary stats row */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {pets.length} Pet{pets.length !== 1 ? 's' : ''} &bull;&nbsp;
              {appointments.length} Appointment{appointments.length !== 1 ? 's' : ''} &bull;&nbsp;
              {invoices.length} Invoice{invoices.length !== 1 ? 's' : ''} &bull;&nbsp;
              Lifetime spend: <strong>${lifetimeSpend.toFixed(2)}</strong>
            </Typography>

            {/* ── Pets tab ── */}
            {tab === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Species</TableCell>
                      <TableCell>Breed</TableCell>
                      <TableCell>Age / DOB</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pets.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No pets registered</TableCell></TableRow>
                    ) : pets.map(pet => (
                      <TableRow key={pet.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/patients/${pet.id}`)}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <span style={{ fontSize: 16 }}>{speciesEmoji(pet.species)}</span>
                            <Typography
                              component="span"
                              color="primary.main"
                              fontWeight={500}
                              sx={{ '&:hover': { textDecoration: 'underline' } }}>
                              {pet.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{pet.species}</TableCell>
                        <TableCell>{pet.breed || '—'}</TableCell>
                        <TableCell>
                          {pet.date_of_birth
                            ? `${Math.floor((Date.now() - new Date(pet.date_of_birth).getTime()) / (365.25 * 86400000))} yrs`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={pet.status} size="small" color={getStatusColor(pet.status) as any} />
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Button size="small" onClick={() => navigate(`/patients/${pet.id}`)}>View</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Appointments tab ── */}
            {tab === 1 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Pet</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Vet</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appointments.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No appointments found</TableCell></TableRow>
                    ) : appointments.slice(0, 20).map(appt => (
                      <TableRow key={appt.id} hover>
                        <TableCell>{fmtDate(appt.appointment_date)}</TableCell>
                        <TableCell>{appt.appointment_time_start?.slice(0, 5) ?? '—'}</TableCell>
                        <TableCell>{appt.pet_name}</TableCell>
                        <TableCell>{appt.service_name || appt.reason_for_visit}</TableCell>
                        <TableCell>{appt.veterinarian_name}</TableCell>
                        <TableCell>
                          <Chip label={appt.status} size="small" color={getStatusColor(appt.status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Invoices tab ── */}
            {tab === 2 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Pet</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center">No invoices found</TableCell></TableRow>
                    ) : invoices.slice(0, 20).map(inv => (
                      <TableRow key={inv.id} hover>
                        <TableCell>{inv.invoice_number}</TableCell>
                        <TableCell>{fmtDate(inv.invoice_date)}</TableCell>
                        <TableCell>{inv.pet_name}</TableCell>
                        <TableCell align="right">${Number(inv.total_amount).toFixed(2)}</TableCell>
                        <TableCell align="right">${Number(inv.amount_paid).toFixed(2)}</TableCell>
                        <TableCell align="right"
                          sx={Number(inv.balance_due) > 0 ? { color: 'error.main', fontWeight: 700 } : {}}>
                          ${Number(inv.balance_due).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip label={inv.payment_status} size="small" color={getStatusColor(inv.payment_status) as any} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Communications tab ── */}
            {tab === 3 && <CommunicationsTab clientId={client.id} />}

            {/* ── Files tab ── */}
            {tab === 4 && <FilesTab clientId={client.id} />}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientDetailPage;
